/**
 * ProducerConsole — the admin's routing cockpit.
 *
 * Send topic → first speaker auto-selected by speaker-scoring.
 * Manual overrides: pick any agent for the next hop.
 * Quick actions: "Ask Quill to rewrite", "Ask Scribe to verify",
 *                "Ask Delta to log", "Close segment", "Next topic".
 */
import React, { useState } from 'react';

const AGENTS = ['nova', 'orion', 'vega', 'atlas', 'scribe', 'quill', 'delta', 'producer'];
const DOMAINS = [
  { id: 'atmospheric', label: 'Atmospheric' },
  { id: 'space_weather', label: 'Space weather' },
  { id: 'seismic', label: 'Seismic' },
  { id: 'biosphere', label: 'Biosphere' },
  { id: 'markets', label: 'Markets' },
  { id: 'analytics_trending', label: 'Analytics trending' },
  { id: 'global', label: 'Global / other' },
];
const SEVERITIES = ['nominal', 'elevated', 'storm', 'severe', 'critical'];

export default function ProducerConsole({
  episode, busy, onTopic, onStep, onRouteTo, onCloseSegment, onPanelRound,
}) {
  const [topic, setTopic] = useState('');
  const [domain, setDomain] = useState('atmospheric');
  const [severity, setSeverity] = useState('nominal');
  const panelOrder = (episode?.panel_order && episode.panel_order.length
    ? episode.panel_order
    : ['nova', 'orion', 'vega', 'atlas']);

  const handleSetTopic = (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    onTopic({
      topic: topic.trim(),
      domain,
      severity,
      sources: [{ service_id: 'svc.producer_console', confidence: 0.75 }],
    });
    setTopic('');
  };

  return (
    <div style={{ fontSize: 12 }}>
      {/* Topic kickoff */}
      <form onSubmit={handleSetTopic} style={{ marginBottom: 16 }}>
        <label style={label}>TOPIC</label>
        <input
          type="text"
          data-testid="producer-topic-input"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g. M7.4 earthquake off Miyako"
          style={input}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
          <div>
            <label style={label}>DOMAIN</label>
            <select
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              style={input}
              data-testid="producer-domain-select"
            >
              {DOMAINS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>SEVERITY</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              style={input}
              data-testid="producer-severity-select"
            >
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" disabled={busy || !episode} style={btnPrimary} data-testid="producer-set-topic-btn">
          {busy ? '…' : 'SET TOPIC & OPEN'}
        </button>
      </form>

      {/* Auto-advance / manual route */}
      <div style={{ marginBottom: 16 }}>
        <label style={label}>AUTO-ADVANCE</label>
        <button
          type="button"
          onClick={() => onStep()}
          disabled={busy || !episode?.next_speaker}
          style={btnAccent}
          data-testid="producer-step-btn"
        >
          {busy ? '…' : `NEXT HOP → ${(episode?.next_speaker || '—').toUpperCase()}`}
        </button>
      </div>

      {/* Cast panel round — Nova → Orion → Vega → Atlas */}
      <div style={{ marginBottom: 16 }}>
        <label style={label}>CAST PANEL ROUND</label>
        <button
          type="button"
          onClick={() => onPanelRound && onPanelRound()}
          disabled={busy || !episode?.active_topic || !onPanelRound}
          style={btnPanel}
          data-testid="producer-panel-round-btn"
        >
          {busy ? '…' : `▶ RUN PANEL · ${panelOrder.map((a) => a.toUpperCase()).join(' → ')}`}
        </button>
        <div style={{ marginTop: 4, fontSize: 9, letterSpacing: '0.14em', color: 'rgba(200,210,230,0.45)', fontFamily: 'ui-monospace, Menlo, monospace' }}>
          Each anchor receives the prior anchor's reply as context.
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={label}>ROUTE TO (MANUAL)</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          {AGENTS.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => onRouteTo(a)}
              disabled={busy || !episode}
              data-testid={`producer-route-${a}`}
              style={{
                ...btnGhost,
                fontSize: 10, letterSpacing: '0.18em',
                opacity: busy || !episode ? 0.45 : 1,
              }}
            >
              {a.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ marginBottom: 16 }}>
        <label style={label}>QUICK ACTIONS</label>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => onRouteTo('quill', 'Rewrite the last beat for air — tighten + broadcast-safe.')}
            disabled={busy || !episode}
            style={btnQuill}
            data-testid="producer-quill-rewrite"
          >
            ASK QUILL · REWRITE
          </button>
          <button
            type="button"
            onClick={() => onRouteTo('scribe', 'Verify the last claim. Call out unsupported ones.')}
            disabled={busy || !episode}
            style={btnScribe}
            data-testid="producer-scribe-verify"
          >
            ASK SCRIBE · VERIFY
          </button>
          <button
            type="button"
            onClick={() => onRouteTo('delta', 'Archive the show-ready segments so far with timestamps.')}
            disabled={busy || !episode}
            style={btnDelta}
            data-testid="producer-delta-archive"
          >
            ASK DELTA · ARCHIVE
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        <button
          type="button"
          onClick={onCloseSegment}
          disabled={busy || !episode?.active_segment}
          style={btnClose}
          data-testid="producer-close-segment"
        >
          ✕ CLOSE SEGMENT
        </button>
      </div>

      {episode?.speaker_queue?.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 10, borderTop: '1px solid rgba(120,150,200,0.18)' }}>
          <label style={label}>RECENT SPEAKERS</label>
          <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: 'rgba(200,210,230,0.7)' }}>
            {episode.speaker_queue.join(' → ')}
          </div>
        </div>
      )}
    </div>
  );
}

const label = {
  display: 'block',
  fontSize: 9,
  letterSpacing: '0.28em',
  color: 'rgba(200,210,230,0.55)',
  marginBottom: 4,
  fontFamily: 'ui-monospace, Menlo, monospace',
};
const input = {
  width: '100%',
  background: 'rgba(10,14,22,0.6)',
  color: '#e5ebf3',
  border: '1px solid rgba(120,150,200,0.25)',
  borderRadius: 4,
  padding: '8px 10px',
  fontSize: 12,
  fontFamily: 'Georgia, serif',
  boxSizing: 'border-box',
};
const btnPrimary = {
  ...input,
  marginTop: 8,
  background: 'linear-gradient(180deg, #d4a57418, #d4a57408)',
  color: '#f3e5c3',
  borderColor: '#d4a574',
  cursor: 'pointer',
  letterSpacing: '0.2em',
  fontWeight: 600,
  fontFamily: 'ui-monospace, Menlo, monospace',
};
const btnAccent = {
  ...input,
  background: '#60a5fa18',
  borderColor: '#60a5fa55',
  color: '#60a5fa',
  cursor: 'pointer',
  letterSpacing: '0.18em',
  fontWeight: 600,
  fontFamily: 'ui-monospace, Menlo, monospace',
};
const btnPanel = {
  ...input,
  background: 'linear-gradient(180deg, rgba(34,211,238,0.16), rgba(239,68,68,0.10))',
  borderColor: 'rgba(34,211,238,0.55)',
  color: '#dbe3f2',
  cursor: 'pointer',
  letterSpacing: '0.14em',
  fontWeight: 600,
  fontFamily: 'ui-monospace, Menlo, monospace',
};
const btnGhost = {
  background: 'rgba(255,255,255,0.04)',
  color: '#e5ebf3',
  border: '1px solid rgba(120,150,200,0.25)',
  borderRadius: 3,
  padding: '6px 4px',
  cursor: 'pointer',
  fontFamily: 'ui-monospace, Menlo, monospace',
};
const btnQuill = { ...btnGhost, color: '#d4a574', borderColor: '#d4a57455', fontSize: 10 };
const btnScribe = { ...btnGhost, color: '#a78bfa', borderColor: '#a78bfa55', fontSize: 10 };
const btnDelta = { ...btnGhost, color: '#94a3b8', borderColor: '#94a3b855', fontSize: 10 };
const btnClose = {
  ...input,
  background: 'rgba(239,68,68,0.08)',
  borderColor: 'rgba(239,68,68,0.55)',
  color: '#ef4444',
  cursor: 'pointer',
  letterSpacing: '0.2em',
  fontFamily: 'ui-monospace, Menlo, monospace',
  fontWeight: 600,
};
